import { useState, useEffect, useCallback } from "react";
import api from "@/utils/axios-utils";
import { WEB_BASE_URL } from "@/utils/apiBaseUrls";
import { useAuthStore } from "@/store/useAuthStore";

const AUTOSAVE_INTERVAL_MS = 60_000;

// Save hook for draft spaces: PATCHes serialized snapshot data to the backend.
// getData() is called at save time — caller decides what to serialize (e.g. editor.getSnapshot()).
export function useTeacherDraftSnapshot(
  spaceId: string | undefined,
  enabled: boolean,
  getData: () => Record<string, unknown> | null,
  initialSavedAt?: Date | null,
): {
  save: () => Promise<void>;
  lastSaved: Date | null;
  markSaved: () => void;
} {
  const token = useAuthStore((s) => s.token);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  useEffect(() => {
    if (initialSavedAt) setLastSaved(initialSavedAt);
  }, [initialSavedAt]);

  const save = useCallback(async () => {
    if (!spaceId) return;
    const snapshot = getData();
    if (!snapshot) return;
    try {
      await api.patch(`/spaces/${spaceId}/draft-snapshot`, { snapshot });
      setLastSaved(new Date());
    } catch (err) {
      console.error("[useTeacherDraftSnapshot] save failed:", err);
    }
  }, [spaceId, getData]);

  useEffect(() => {
    if (!enabled || !spaceId) return;
    const timer = setInterval(save, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [enabled, spaceId, save]);

  useEffect(() => {
    if (!enabled || !spaceId || !token) return;
    // /spaces/* is served by apps/web. Raw fetch rather than the axios client
    // because this runs on beforeunload and needs keepalive.
    const apiUrl = WEB_BASE_URL;
    const handleUnload = () => {
      const snapshot = getData();
      if (!snapshot) return;
      // keepalive: true is best-effort — browsers cap the payload at ~64KB.
      // Large snapshots may be silently dropped; the 60s autosave is the primary safety net.
      fetch(`${apiUrl}/spaces/${spaceId}/draft-snapshot`, {
        method: "PATCH",
        keepalive: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ snapshot }),
      });
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [enabled, spaceId, token, getData]);

  const markSaved = useCallback(() => setLastSaved(new Date()), []);

  return { save, lastSaved, markSaved };
}
